import { z } from 'zod/v4';

import { MonetaryValueBackendRuntype } from '../backendTypes';
import { DataSourcesDTORuntype } from '../dataSource';
import { PackagingRuntype, PriceTypeRuntype } from '../offer';
import { ScrapRulesDTOInsertRuntype, ScrapRulesDTORuntype } from '../scrap';
import {
    AbcClassificationSettingsRuntype,
    CurrencySettingDTORuntype,
    CurrencySettingInputDTORuntype,
} from '../sourcingTemplate';
import { TotalCostOfOwnershipInputDTORuntype, TotalCostOfOwnershipRuleDTORuntype } from '../totalCostOfOwnershipRules';

export const PartAlternativeStrategyRuntype = z.enum([
    'IncludePartAlternativesWithoutWarning',
    'IncludePartAlternativesAsWarning',
    'ExcludePartAlternatives',
]);
export type PartAlternativeStrategy = z.infer<typeof PartAlternativeStrategyRuntype>;

export enum LimitType {
    Never = 'Never',
    Days = 'Days',
}

const LimitTypeEnum = z.enum(LimitType);

export const LimitRuntype = z.object({
    type: LimitTypeEnum,
    number: z.number().optional(),
});

export interface ExcessPreferenceDTO extends z.infer<typeof ExcessPreferenceDTORuntype> {}

export const ExcessPreferenceDTORuntype = z.object({
    max_ratio_percent: z.number().nullable(),
    minimum_savings: MonetaryValueBackendRuntype.nullable(),
});

export interface OfferAgeLimitDTO extends z.infer<typeof OfferAgeLimitDTORuntype> {}
export const OfferAgeLimitDTORuntype = z.object({
    expiring_before_validity_date: LimitRuntype,
    expired_after_validity_date: LimitRuntype,
    old_after_update: LimitRuntype,
    outdated_after_update: LimitRuntype,
});

export enum SourcingPreferenceOptions {
    Fastest = 'Fastest',
    BestPrice = 'BestPrice',
    BestPriceBy = 'BestPriceBy',
}

export const SourcingPreferenceOptionsRuntype = z.enum(SourcingPreferenceOptions);

export const LeadTimePreferenceRuntype = z.object({
    type: SourcingPreferenceOptionsRuntype,
    target: z.string().optional(),
});

export const InventoryPreferenceRuntype = z.enum(['PreferInventory', 'Neutral', 'PreferMarketOffers']);
export type InventoryPreferenceType = z.infer<typeof InventoryPreferenceRuntype>;

export const SolutionPreferenceDTORuntype = z.object({
    lead_time_preference: LeadTimePreferenceRuntype,
    preferred_suppliers_and_stock_locations: z.null().or(z.array(z.string())),
    approved_suppliers_and_stock_locations: z.null().or(z.array(z.string())),
    excluded_suppliers_and_stock_locations: z.null().or(z.array(z.string())),
    preferred_inventory_sites: z.null().or(z.array(z.string())),
    approved_inventory_sites: z.null().or(z.array(z.string())),
    excluded_inventory_sites: z.null().or(z.array(z.string())),
    offer_age_limit: OfferAgeLimitDTORuntype,
    inventory_preference: InventoryPreferenceRuntype,
    use_unit_price: z.boolean(),
    packaging_preference: z.array(PackagingRuntype),
    prefer_direct_api_integrations: z.boolean(),
    excluded_price_types: z.array(PriceTypeRuntype),
    exclude_mismatched_price_points: z.boolean(),
    prioritize_preferred_manufacturers: z.boolean(),
    prefer_customer_negotiated_prices: z.boolean(),
    total_cost_of_ownership_rules: TotalCostOfOwnershipRuleDTORuntype.array(),
    currency_settings: CurrencySettingDTORuntype,
    scrap_rules: ScrapRulesDTORuntype,
    abc_classification_settings: AbcClassificationSettingsRuntype,
    warn_when_missing_offer_validity_dates: z.boolean().optional(),
    exclude_offers_outside_validity_window: z.boolean().optional(),
    offer_validation_window: z.object({ start: z.string(), end: z.string() }).nullable().optional(),
    data_sources: DataSourcesDTORuntype,
    part_alternative_strategy: PartAlternativeStrategyRuntype,
    declare_re_reeling_costs_as_tco: z.boolean(),
});
export interface SolutionPreferenceDTO extends z.infer<typeof SolutionPreferenceDTORuntype> {}

export interface SolutionPreferenceInputDTO extends z.infer<typeof SolutionPreferenceInputDTORuntype> {}
export const SolutionPreferenceInputDTORuntype = z.object({
    lead_time_preference: LeadTimePreferenceRuntype,
    preferred_suppliers_and_stock_locations: z.null().or(z.array(z.string())),
    approved_suppliers_and_stock_locations: z.null().or(z.array(z.string())),
    excluded_suppliers_and_stock_locations: z.null().or(z.array(z.string())),
    preferred_inventory_sites: z.null().or(z.array(z.string())),
    approved_inventory_sites: z.null().or(z.array(z.string())),
    excluded_inventory_sites: z.null().or(z.array(z.string())),
    offer_age_limit: OfferAgeLimitDTORuntype,
    inventory_preference: InventoryPreferenceRuntype,
    use_unit_price: z.boolean(),
    packaging_preference: z.array(PackagingRuntype),
    prefer_direct_api_integrations: z.boolean(),
    excluded_price_types: z.array(PriceTypeRuntype),
    exclude_mismatched_price_points: z.boolean(),
    prioritize_preferred_manufacturers: z.boolean(),
    prefer_customer_negotiated_prices: z.boolean(),
    total_cost_of_ownership_rules: TotalCostOfOwnershipInputDTORuntype.array(),
    currency_settings: CurrencySettingInputDTORuntype,
    scrap_rules: ScrapRulesDTOInsertRuntype,
    abc_classification_settings: AbcClassificationSettingsRuntype,
    warn_when_missing_offer_validity_dates: z.boolean(),
    exclude_offers_outside_validity_window: z.boolean(),
    offer_validation_window: z.object({ start: z.string(), end: z.string() }).nullable(),
    data_sources: DataSourcesDTORuntype,
    part_alternative_strategy: PartAlternativeStrategyRuntype,
    declare_re_reeling_costs_as_tco: z.boolean(),
});
